import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormikContext } from "formik";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";
import { ScaledIframe } from "newui/components/ScaledIframe";
import { PreviewPageCard } from "newui/components/PreviewPageCard";
import PdfPreviewZoomBar from "newui/components/PdfPreviewZoomBar";
import { useRfxResponsePreviewHtml } from "../../hooks/useRfxResponsePreviewHtml";
import { useSampleResponsePreviewHtml } from "../../hooks/useSampleResponsePreviewHtml";
import type { PdfSettingsFormValues } from "newui/features/Settings/PdfSettings/types/PdfSettingsForm.type";
import type { RfxReportPreviewHtmlResponse } from "../../types";

interface RfxResponsePreviewPanelProps {
	rfxId?: number;
	inviteId?: number;
}

const CSS_VAR_MAP: Record<string, string> = {
	colour_primary: "--brand-primary",
	colour_secondary: "--brand-secondary",
	colour_text: "--brand-text",
	colour_header_bg: "--brand-header-bg",
	colour_footer_bg: "--brand-footer-bg",
	font_heading: "--font-heading",
	font_body: "--font-body",
	font_size_base_pt: "--font-size-base",
	header_divider_colour: "--header-divider-colour",
	footer_divider_colour: "--footer-divider-colour",
};

const STRUCTURAL_FIELDS: (keyof PdfSettingsFormValues)[] = [
	"header_enabled",
	"header_left",
	"header_centre",
	"header_right",
	"header_left_text",
	"header_centre_text",
	"header_right_text",
	"header_divider_enabled",
	"header_divider_weight_pt",
	"footer_enabled",
	"footer_left",
	"footer_centre",
	"footer_right",
	"footer_left_text",
	"footer_centre_text",
	"footer_right_text",
	"footer_page_number_format",
	"footer_divider_enabled",
	"footer_divider_weight_pt",
	"cover_page_mode",
	"cover_show_logo",
	"cover_show_company_name",
	"cover_company_name_colour",
	"logo_max_height_px",
];

const formatCssValue = (key: string, value: unknown): string => {
	if (key === "font_size_base_pt") {
		return `${value}pt`;
	}
	if (key === "font_heading" || key === "font_body") {
		return `'${value}', sans-serif`;
	}
	return String(value);
};

const STRUCTURAL_DEBOUNCE_MS = 800;
const A4_PORTRAIT = { width: 794, height: 1123 };
const A4_LANDSCAPE = { width: 1123, height: 794 };
const PREVIEW_DEFAULT_WIDTH = 560;
const PREVIEW_MIN_WIDTH = 320;
const PREVIEW_MAX_WIDTH = 1200;

export const RfxResponsePreviewPanel: React.FC<RfxResponsePreviewPanelProps> = ({ rfxId, inviteId }) => {
	const { values } = useFormikContext<PdfSettingsFormValues>();
	const realMutation = useRfxResponsePreviewHtml();
	const sampleMutation = useSampleResponsePreviewHtml();
	const [previewData, setPreviewData] = useState<RfxReportPreviewHtmlResponse | null>(null);
	const [previewWidth, setPreviewWidth] = useState(PREVIEW_DEFAULT_WIDTH);
	const structuralDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const prevStructuralRef = useRef<string>("");
	const realMutateRef = useRef(realMutation.mutate);
	realMutateRef.current = realMutation.mutate;
	const sampleMutateRef = useRef(sampleMutation.mutate);
	sampleMutateRef.current = sampleMutation.mutate;
	const valuesRef = useRef(values);
	valuesRef.current = values;

	const cssVarConfig = useMemo(() => ({
		formValues: values as unknown as Record<string, unknown>,
		varMap: CSS_VAR_MAP,
		formatValue: formatCssValue,
	}), [values]);

	const structuralFingerprint = useMemo(() => {
		const vals: Record<string, unknown> = {};
		for (const key of STRUCTURAL_FIELDS) {
			vals[key] = values[key];
		}
		return JSON.stringify(vals);
	}, [values]);

	const fetchPreviewHtml = useCallback(() => {
		const currentValues = valuesRef.current;
		const schema: Record<string, unknown> = {};
		for (const key of Object.keys(currentValues) as (keyof PdfSettingsFormValues)[]) {
			if (key === "shortlist_visible_sections" || key === "shortlist_visible_fields") {
				continue;
			}
			const val = currentValues[key];
			if (val !== null && val !== undefined) {
				schema[key] = val;
			}
		}

		const onSuccess = (data: RfxReportPreviewHtmlResponse) => setPreviewData(data);

		if (rfxId != null) {
			realMutateRef.current(
				{ rfxId, params: { design_schema: schema, invite_id: inviteId } },
				{ onSuccess },
			);
		} else {
			sampleMutateRef.current(
				{ design_schema: schema },
				{ onSuccess },
			);
		}
	}, [rfxId, inviteId]);

	useEffect(() => {
		if (prevStructuralRef.current === "") {
			prevStructuralRef.current = structuralFingerprint;
			fetchPreviewHtml();
			return;
		}

		if (prevStructuralRef.current !== structuralFingerprint) {
			prevStructuralRef.current = structuralFingerprint;
			if (structuralDebounceRef.current) {
				clearTimeout(structuralDebounceRef.current);
			}
			structuralDebounceRef.current = setTimeout(fetchPreviewHtml, STRUCTURAL_DEBOUNCE_MS);
		}

		return () => {
			if (structuralDebounceRef.current) {
				clearTimeout(structuralDebounceRef.current);
			}
		};
	}, [structuralFingerprint, fetchPreviewHtml]);

	const isUpdating = realMutation.isPending || sampleMutation.isPending;

	if (!previewData) {
		return (
			<div className="sticky top-0 flex flex-col bg-gray-100 p-6 rounded-lg h-full items-center">
				<LoadingSpinner label="Loading preview..." verticalPadding={4} />
			</div>
		);
	}

	const showCoverPage = values.cover_page_mode === "default";
	const hasCoverPage = values.cover_page_mode !== "none";
	const showHeader = values.header_enabled;
	const showFooter = values.footer_enabled;
	const totalPages = (hasCoverPage ? 1 : 0) + previewData.pages.length;

	return (
		<>
			<PdfPreviewZoomBar
				currentPreviewWidth={previewWidth}
				previewDefaultWidth={PREVIEW_DEFAULT_WIDTH}
				previewMaxWidth={PREVIEW_MAX_WIDTH}
				previewMinWidth={PREVIEW_MIN_WIDTH}
				totalPages={totalPages}
				setPreviewWidth={setPreviewWidth}
				showSampleDataBanner={rfxId == null}
			/>
			<div className="bg-gray-100 overflow-x-auto">
				<div className="flex flex-col p-6 rounded-lg h-full" style={{ minWidth: "fit-content" }}>
					{isUpdating && (
						<LoadingSpinner
							label="Updating preview..."
							spinnerSize="sm"
							fontSize="sm"
							horizontal
							verticalPadding={2}
						/>
					)}

					<div className="flex flex-col gap-4">
						{showCoverPage && previewData.cover_html && (
							<PreviewPageCard pageLabel="Page 1 — Cover Page" previewWidth={previewWidth}>
								<ScaledIframe
									srcDoc={previewData.cover_html}
									title="Cover page preview"
									nativeWidth={A4_PORTRAIT.width}
									previewWidth={previewWidth}
									minHeight={A4_PORTRAIT.height}
									cssVarConfig={cssVarConfig}
									pageNum={1}
									totalPages={totalPages}
								/>
							</PreviewPageCard>
						)}

						{values.cover_page_mode === "upload" && (
							<PreviewPageCard pageLabel="Page 1 — Cover Page" previewWidth={previewWidth}>
								<div className="flex flex-col items-center justify-center py-16 px-6 text-center">
									<p className="text-sm font-medium text-gray-700 mb-1">User Uploaded Cover Page</p>
									<p className="text-xs text-gray-500">
										Use &quot;Preview Full Report&quot; to view the uploaded cover page
									</p>
								</div>
							</PreviewPageCard>
						)}

						{previewData.pages.map((page, index) => {
							const pageNum = (hasCoverPage ? 2 : 1) + index;
							const dims = page.orientation === "landscape" ? A4_LANDSCAPE : A4_PORTRAIT;
							return (
								<PreviewPageCard key={page.label || index} pageLabel={`Page ${pageNum} — ${page.label}`} previewWidth={previewWidth}>
									{showHeader && previewData.header_html && (
										<ScaledIframe
											srcDoc={previewData.header_html}
											title={`Header page ${pageNum}`}
											nativeWidth={dims.width}
											previewWidth={previewWidth}
											fixedHeight={40}
											cssVarConfig={cssVarConfig}
											pageNum={pageNum}
											totalPages={totalPages}
										/>
									)}
									<ScaledIframe
										srcDoc={page.html}
										title={`${page.label} preview`}
										nativeWidth={dims.width}
										previewWidth={previewWidth}
										minHeight={dims.height}
										cssVarConfig={cssVarConfig}
										pageNum={pageNum}
										totalPages={totalPages}
									/>
									{showFooter && previewData.footer_html && (
										<ScaledIframe
											srcDoc={previewData.footer_html}
											title={`Footer page ${pageNum}`}
											nativeWidth={dims.width}
											previewWidth={previewWidth}
											fixedHeight={36}
											cssVarConfig={cssVarConfig}
											pageNum={pageNum}
											totalPages={totalPages}
										/>
									)}
								</PreviewPageCard>
							);
						})}
					</div>
				</div>
			</div>
		</>
	);
};
